<script lang="ts" setup>
import { vLazyVideo } from '@/composables/useLazyVideo'
const { trackEvent } = useCnzz()

const { t } = useI18n()

definePageMeta({ layout: 'page' })
useHead({
  title: t('city.pageTitle'),
  meta: [
    {
      name: 'description',
      content: t('seo.city.description'),
    },
    {
      name: 'keywords',
      content: t('seo.city.keywords'),
    },
    {
      property: 'og:title',
      content: t('seo.city.title'),
    },
    {
      property: 'og:description',
      content: t('seo.city.description'),
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      property: 'og:locale',
      content: 'zh_CN',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: t('seo.city.title'),
    },
    {
      name: 'twitter:description',
      content: t('seo.city.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      name: 'author',
      content: '玻色量子',
    },
  ],
})

// 四个量子模块数据，包含轮播项数量
const quantumSections = [
  {
    key: 'energy',
    video: 'https://images-cloud.qboson.com/website/energy.png',
    link: 'https://advanced.onlinelibrary.wiley.com/doi/10.1002/qute.202500241',
    isVideo: false,
    imageLeft: false,
    carouselCount: 2,
  },
  {
    key: 'finance',
    video: 'https://images-cloud.qboson.com/website/city/finance.png',
    link: 'https://doi.org/10.3390/e26121026',
    isVideo: false,
    imageLeft: true,
    carouselCount: 2,
  },
  {
    key: 'communication',
    video: 'https://images-cloud.qboson.com/website/communication.png',
    link: 'https://ieeexplore.ieee.org/document/10436892',
    isVideo: false,
    imageLeft: false,
    carouselCount: 2,
  },
  {
    key: 'traffic',
    video: 'https://images-cloud.qboson.com/website/traffic.png',
    link: 'https://www.mdpi.com/1099-4300/27/9/953',
    isVideo: false,
    imageLeft: true,
    carouselCount: 2,
  },
]

// 在线咨询弹窗逻辑
const showConsultDialog = ref(false)

// 打开弹窗
const openDialog = () => {
  showConsultDialog.value = true
  trackEvent('数字城市', '点击', '前往咨询按钮')
}

// 了解更多 - 友盟上报
const trackEventMore = (item: any) => {
  const subtitle = t(`city.sections.${item.key}.subtitle`)
  trackEvent('数字城市', '点击', `${subtitle}-了解更多按钮`)
}

// 了解更多 - 友盟上报
const trackEventCarouselCount = (item: any, index: number) => {
  const title = t(`city.sections.${item.key}.carouselItems[${index - 1}].title`)
  trackEvent('数字城市', '点击', `${title}-了解更多按钮`)
}
</script>

<template>
  <div class="w-full bg-white">
    <!-- Hero 区域 -->
    <section class="relative overflow-hidden">
      <!-- 背景图片 - web端 -->
      <div class="hidden md:block w-full">
        <video
          v-lazy-video
          data-src="https://images-cloud.qboson.com/website/city.mp4"
          loop
          muted
          playsinline
          disablepictureinpicture
        ></video>
      </div>
      <!-- 背景图片 - 移动端 -->
      <div class="md:hidden w-full">
        <img
          class="w-full h-auto"
          src="https://images-cloud.qboson.com/website/cityMobile.png"
          alt=""
        />
      </div>

      <!-- 内容 -->
      <div
        class="absolute inset-0 z-10 md:max-w-[1200px] mx-auto h-full flex flex-col px-[16px] md:px-0 mt-[70px] md:mt-[140px]"
      >
        <p
          class="text-[24px] md:text-[45px] font-medium mb-[10px] md:mb-[25px]"
        >
          {{ t('city.hero.title') }}
        </p>
        <p
          class="text-xs md:text-xl font-normal md:max-w-[548px] md:leading-[34px]"
        >
          {{ t('city.hero.description') }}
        </p>
        <!-- 前往咨询按钮 -->
        <NuxtLink
          to="/product/productConsultation"
          class="md:hidden bg-[#FFFFFF99] rounded-[12px] w-[104px] text-[14px] font-medium text-center py-[8px] mt-[20px] mb-[200px] join-border text-[#000] cursor-pointer inline-block"
        >
          {{ t('city.hero.btnConsult') }}
        </NuxtLink>
        <div
          class="hidden md:block bg-transparent w-[144px] text-[16px] font-medium text-center py-[12px] mt-[63px] mb-[200px] join-border text-[#333] cursor-pointer"
          @click="openDialog"
        >
          {{ t('city.hero.btnConsult') }}
        </div>
      </div>
    </section>

    <!-- 量子模块遍历 -->
    <section
      v-for="(section, sectionIndex) in quantumSections"
      :key="section.key"
      class="py-[40px] md:py-[60px]"
      :class="sectionIndex % 2 === 0 ? 'bg-white' : 'bg-[#DCE2EE]'"
    >
      <div class="max-w-[1200px] mx-auto px-6">
        <p
          class="text-[16px] md:text-[32px] font-medium md:font-bold text-[#000] md:text-[#333] text-center mb-[32px] md:mb-12"
        >
          {{ $t(`city.sections.${section.key}.title`) }}
        </p>

        <div
          class="flex flex-col md:flex-row justify-between items-center gap-8 md:gap-16"
          :class="{ 'md:flex-row-reverse': !section.imageLeft }"
        >
          <!-- 视频/图片 -->
          <div class="w-full md:w-1/2 flex items-center justify-center">
            <div class="image-shadow-wrapper">
              <img
                v-if="!section.isVideo"
                class="w-full h-full"
                :src="section.video"
                alt=""
              />
              <video
                v-if="section.isVideo"
                v-lazy-video
                class="w-full h-[324px] object-contain"
                :data-src="section.video"
                loop
                muted
                playsinline
                disablepictureinpicture
              ></video>
            </div>
          </div>

          <!-- 文字内容 -->
          <div
            class="bg-white w-full md:w-[calc(50%-100px)] rounded-[12px] p-[15px] md:py-[45px] md:px-[50px] border-[1px] border-[#eee] shadow-[4px_4px_12px_0px_rgba(87,126,211,0.08)] md:shadow-[4px_4px_12px_0_rgba(87, 126, 211, 0.08)]"
          >
            <p
              v-if="$t(`city.sections.${section.key}.subtitle`) !== ''"
              class="pl-[27px] md:pl-0 mb-[12px] md:mb-[27px] text-[14px] md:text-[20px] text-[#000] md:text-[#193757] font-medium"
            >
              {{ $t(`city.sections.${section.key}.subtitle`) }}
            </p>

            <!-- web端 -->
            <div
              v-for="itemIndex in 3"
              :key="itemIndex"
              class="hidden md:flex items-center text-sm md:text-base text-[#666] leading-relaxed"
              :class="{ 'mb-4': itemIndex < 3 }"
            >
              <div
                class="w-[11px] h-[11px] rounded-[50%] mr-[20px] flex-shrink-0"
                style="
                  background: linear-gradient(
                    124deg,
                    #36ba93 -2.73%,
                    #37a9c5 50.34%,
                    #073c89 103.64%
                  );
                "
              ></div>
              <p>
                {{ $t(`city.sections.${section.key}.items[${itemIndex - 1}]`) }}
              </p>
            </div>

            <!-- 移动端 -->
            <div
              v-for="itemIndex in 3"
              :key="itemIndex"
              class="md:hidden flex items-center text-sm md:text-base text-[#666] leading-relaxed"
              :class="{ 'mb-[5px]': itemIndex < 3 }"
            >
              <div
                class="w-[5px] h-[5px] rounded-[1px] mr-[20px] flex-shrink-0 bg-[#349CC7]"
              ></div>
              <p class="text-[12px] text-[#000] font-normal leading-[18px]">
                {{ $t(`city.sections.${section.key}.items[${itemIndex - 1}]`) }}
              </p>
            </div>

            <!-- 查看更多 -->
            <a
              :href="section.link"
              target="_blank"
              class="learn-more-border w-[110px] md:w-[128px] flex items-center justify-between cursor-pointer text-xs text-[#000] leading-[18px] font-normal py-[10px] px-[20px] rounded-[12px] mt-[16px] md:mt-[30px] ml-[27px] md:ml-0"
              @click="trackEventMore(section)"
            >
              <span class="w-full text-center">{{
                $t('common.learnMore')
              }}</span>
            </a>
          </div>
        </div>

        <!-- 轮播 -->
        <div class="hidden relative md:flex items-center mt-[20px]">
          <!-- 轮播内容 -->
          <div
            class="border border-[#eee] bg-white flex items-center justify-center flex-1 overflow-hidden rounded-[12px]"
            style="box-shadow: 4px 4px 12px 0 rgba(87, 126, 211, 0.08)"
          >
            <div class="flex gap-[20px]">
              <div
                v-for="index in section.carouselCount"
                :key="index"
                class="relative w-[580px] flex-shrink-0 bg-white py-[25px] px-[40px] flex items-center justify-between"
                :class="{ 'carousel-divider': index < section.carouselCount }"
              >
                <!-- 标题文字 -->
                <p class="text-[16px] font-normal text-[#000] leading-[26px]">
                  {{
                    $t(
                      `city.sections.${section.key}.carouselItems[${index - 1}].title`,
                    )
                  }}
                </p>
                <!-- 了解更多 -->
                <a
                  v-if="
                    $t(
                      `city.sections.${section.key}.carouselItems[${index - 1}].isShowArrow`,
                    ) === 'true'
                  "
                  :href="
                    $t(
                      `city.sections.${section.key}.carouselItems[${index - 1}].subLink`,
                    ) !== ''
                      ? $t(
                          `city.sections.${section.key}.carouselItems[${index - 1}].subLink`,
                        )
                      : '#'
                  "
                  target="_blank"
                  class="flex items-center gap-[8px] text-[14px] font-normal flex-shrink-0"
                  style="
                    background: linear-gradient(
                      90deg,
                      #37bb94 0%,
                      #012e80 100%
                    );
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                  "
                  @click="trackEventCarouselCount(section, index)"
                >
                  <span>{{ $t('common.learnMore') }}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="5"
                    viewBox="0 0 15 5"
                    fill="none"
                  >
                    <path d="M0 4.38281H13L8.21053 0.382812" stroke="#083E8A" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- 移动端轮播 -->
        <div class="relative md:hidden flex items-center mt-[20px]">
          <!-- 轮播内容 -->
          <div class="w-full overflow-x-auto scrollbar-hide">
            <div class="flex flex-col gap-[10px]">
              <div
                v-for="index in section.carouselCount"
                :key="index"
                class="w-full flex-shrink-0 bg-white rounded-[12px] py-[16px] px-[16px] flex items-center justify-between border border-[#eee] shadow-[4px_4px_12px_0px_rgba(87,126,211,0.08)]"
              >
                <!-- 标题文字 -->
                <p class="text-[12px] font-normal text-[#000] leading-[18px]">
                  {{
                    $t(
                      `city.sections.${section.key}.carouselItems[${index - 1}].title`,
                    )
                  }}
                </p>
                <!-- 了解更多 -->
                <a
                  v-if="
                    $t(
                      `city.sections.${section.key}.carouselItems[${index - 1}].isShowArrow`,
                    ) === 'true'
                  "
                  :href="
                    $t(
                      `city.sections.${section.key}.carouselItems[${index - 1}].subLink`,
                    ) !== ''
                      ? $t(
                          `city.sections.${section.key}.carouselItems[${index - 1}].subLink`,
                        )
                      : '#'
                  "
                  target="_blank"
                  class="flex items-center gap-[4px] text-[12px] font-normal flex-shrink-0"
                  style="
                    background: linear-gradient(
                      90deg,
                      #37bb94 0%,
                      #012e80 100%
                    );
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                  "
                  @click="trackEventCarouselCount(section, index)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="6"
                    viewBox="0 0 15 6"
                    fill="none"
                  >
                    <path d="M0 4.80859H13.5L10 0.308594" stroke="#083E8A" />
                  </svg>
                </a>
              </div>
              <!-- 右侧占位元素 -->
              <div class="w-[16px] flex-shrink-0"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 业务咨询及商务合作弹窗 -->
    <ConsultationDialog v-model:show="showConsultDialog" />
  </div>
</template>

<style scoped>
.join-border {
  position: relative;
  z-index: 1;
}

.join-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  z-index: -1;
}

.learn-more-border {
  position: relative;
  z-index: 1;
}

.learn-more-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  z-index: -1;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.carousel-divider::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 50px;
  background-color: #eee;
}

.image-shadow-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.image-shadow-wrapper::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(233, 236, 240, 0.6) 0%,
    rgba(233, 236, 240, 0.3) 40%,
    transparent 70%
  );
  filter: blur(20px);
  z-index: -1;
  transform: scale(1.15);
}
</style>
